/* eslint-disable no-nested-ternary */
import * as React from 'react';
import { Flex, Button, Text } from '@chakra-ui/react';
import moment from 'moment';

import { currencies } from 'data';
import {
  DateInputWithConfirm,
  UploadActions,
  UploadButtonSection,
  UploadExample,
  UploadComplete as UploadCompleteSection,
  AutocompleteInputWithConfirm,
  ModalColumns,
  AcknowledgeBox,
  getStringFromDate,
  getDateFromString,
  RequirementsRemedy,
  RadioOptionsWithConfirm,
  IQuestion
} from 'shared-components';

import UploadIntro from '../../../../atoms/upload-intro';
import bankStatementExample from '../../../../../assets/images/sample-bank-statement.jpg';
import { formatCurrency } from '../../../../../utilities/formatting';
import { getMaturityDate, isPastDate } from '../../../../../utilities/validation';
import { BankStatementDoc, FamilySponsorType, IDate } from '../../../../../types';
import { bankStatementName, bankStatementTransName } from '../../../../../utilities/documents';

import getHighlightedErrorSections from './getHighlightedErrorSections';
import Requirements from './requirements';

interface ICurrency {
  symbol: string;
  name: string;
  symbol_native: string;
  decimal_digits: number;
  rounding: number;
  code: string;
  name_plural: string;
}

interface IProps {
  sponsorType: FamilySponsorType;
  accountOwner: string;
  sectionTitle: string;
  sponsorAmount: number;
  fileNames: string[];
  document: BankStatementDoc;
  onAddRequiredDocument: (howToFixAction: string) => void;
  onContinue: () => void;
  onUpload: (metaData: any) => void;
  onClose: () => void;
  docKey: string;
  infoOnly: boolean;
  docStatus: string;
  onAcknowledgeToggle: (status: string, infoOnlyRequirementsOption?: string, metadata?: any) => void;
}
interface IState {
  infoOnlyRequirementsOption?: string;
  hasBankStatement?: boolean;
  hasBankStatementError: string;
  isPaperBankStatement?: boolean;
  isPaperBankStatementError: string;
  isLogoShown?: boolean;
  isLogoShownError: string;
  isDateShown?: boolean;
  isDateShownError: string;
  isNameAccShown?: boolean;
  isNameAccShownError: string;
  isInEnglish?: boolean;
  isInEnglishError: string;
  hasProvidedTranslation?: boolean;
  hasProvidedTranslationError: string;
  currency?: string;
  currencyError: string;
  currencySuggestions: string[];
  hasAmountInAccount?: boolean;
  hasAmountInAccountError: string;
  belowReqAmount?: boolean;
  belowReqAmountError: string;
  dateBelowReqAmount: IDate;
  dateBelowReqAmountError: string;
  hasRunningTransactions?: boolean;
  hasRunningTransactionsError: string;
  hasClosingBalanceDate?: boolean;
  hasClosingBalanceDateError: string;
  dateOfClosingBalance: IDate;
  dateOfClosingBalanceError: string;
  howToFixAction: string;
  howToFixActionError: string;
  questionsComplete: boolean;
  highlightedErrorSections: string[];
  hasErrors: boolean;
  maturityBelowReqAmountError: boolean;
  maturityClosingBalanceError: boolean;
  fixPossibleCount: number;
  fixHardCount: number;
  translationRequiredCount: number;
}

class BankStatementModal extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    const {
      infoOnlyRequirementsOption,
      isPaperBankStatement,
      isLogoShown,
      isDateShown,
      isNameAccShown,
      isInEnglish,
      hasProvidedTranslation,
      currency,
      hasAmountInAccount,
      belowReqAmount,
      dateBelowReqAmount,
      hasRunningTransactions,
      hasClosingBalanceDate,
      dateOfClosingBalance,
      howToFixAction
    } = props.document;

    const questionsComplete =
      isPaperBankStatement !== undefined &&
      isLogoShown !== undefined &&
      isDateShown !== undefined &&
      isNameAccShown !== undefined &&
      isInEnglish !== undefined &&
      ((isInEnglish === true && hasProvidedTranslation === undefined) ||
        (isInEnglish === false && hasProvidedTranslation !== undefined)) &&
      currency !== undefined &&
      hasAmountInAccount !== undefined &&
      ((hasAmountInAccount === false && belowReqAmount === undefined) ||
        (hasAmountInAccount === true &&
          belowReqAmount === true &&
          dateBelowReqAmount !== undefined &&
          dateBelowReqAmount !== '') ||
        (hasAmountInAccount === true &&
          belowReqAmount === false &&
          (dateBelowReqAmount === undefined || dateBelowReqAmount === '')) ||
        (hasAmountInAccount === true && belowReqAmount !== undefined)) &&
      hasRunningTransactions !== undefined &&
      hasClosingBalanceDate !== undefined &&
      ((hasClosingBalanceDate === true && dateOfClosingBalance !== undefined) ||
        (hasClosingBalanceDate === false && (dateOfClosingBalance === '' || dateOfClosingBalance === undefined)));

    this.state = {
      infoOnlyRequirementsOption: infoOnlyRequirementsOption,
      hasBankStatement: questionsComplete ? true : undefined,
      hasBankStatementError: '',
      isPaperBankStatement: undefined,
      isPaperBankStatementError: '',
      isLogoShown: undefined,
      isLogoShownError: '',
      isDateShown: undefined,
      isDateShownError: '',
      isNameAccShown: undefined,
      isNameAccShownError: '',
      isInEnglish: undefined,
      isInEnglishError: '',
      hasProvidedTranslation: undefined,
      hasProvidedTranslationError: '',
      currency: undefined,
      currencyError: '',
      currencySuggestions: [],
      hasAmountInAccount: undefined,
      hasAmountInAccountError: '',
      belowReqAmount: undefined,
      belowReqAmountError: '',
      dateBelowReqAmount: getDateFromString(dateBelowReqAmount),
      dateBelowReqAmountError: '',
      hasRunningTransactions: undefined,
      hasRunningTransactionsError: '',
      hasClosingBalanceDate: undefined,
      hasClosingBalanceDateError: '',
      dateOfClosingBalance: getDateFromString(dateOfClosingBalance),
      dateOfClosingBalanceError: '',
      questionsComplete: questionsComplete,
      howToFixAction: howToFixAction ? howToFixAction : '',
      howToFixActionError: '',
      highlightedErrorSections: [],
      hasErrors: false,
      maturityBelowReqAmountError: false,
      maturityClosingBalanceError: false,
      fixPossibleCount: 0,
      fixHardCount: 0,
      translationRequiredCount: 0
    };
  }

  componentDidMount() {
    if (this.state.questionsComplete) {
      const highlightedErrorSections = getHighlightedErrorSections(this.props.document);

      // get the correct options message
      const fixPossibleCount = highlightedErrorSections.includes('1.4') ? 1 : 0;
      const fixHardCount =
        (highlightedErrorSections.includes('1.1') ? 1 : 0) +
        (highlightedErrorSections.includes('1.2') ? 1 : 0) +
        (highlightedErrorSections.includes('2.1') ? 1 : 0) +
        (highlightedErrorSections.includes('4.1') ? 1 : 0) +
        (highlightedErrorSections.includes('4.2') ? 1 : 0);
      const translationRequiredCount = ['3.1', '3.2', '3.3', '3.4'].filter(val =>
        highlightedErrorSections.includes(val)
      ).length
        ? 1
        : 0;

      const maturityBelowReqAmountError =
        ['5.1', '5.2', '5.3'].filter(val => highlightedErrorSections.includes(val)).length > 0;
      const maturityClosingBalanceError = maturityBelowReqAmountError || highlightedErrorSections.includes('4.2');

      this.setState({
        maturityBelowReqAmountError,
        maturityClosingBalanceError,
        fixPossibleCount,
        fixHardCount,
        translationRequiredCount,
        highlightedErrorSections,
        howToFixAction: this.props.document.howToFixAction || '',
        hasErrors: this.props.document.isDocumentFixed ? false : highlightedErrorSections.length > 0
      });
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    if (this.state.howToFixAction !== nextProps.document.howToFixAction) {
      this.setState({
        howToFixAction: nextProps.document.howToFixAction || ''
      });
    }
  }

  onQuestionButtonClick = (question: IQuestion, stateName: string) => {
    switch (stateName) {
      case 'isPaperBankStatement': {
        const isPaperBankStatement = question.label === 'Paper';
        if (this.state[stateName] !== isPaperBankStatement)
          this.setState(prevState => ({
            ...prevState,
            [stateName]: isPaperBankStatement
          }));
        break;
      }
      case 'isInEnglish': {
        const isInEnglish = question.label === 'Yes';
        if (this.state[stateName] !== isInEnglish)
          this.setState(prevState => ({
            ...prevState,
            [stateName]: isInEnglish,
            hasProvidedTranslation: undefined
          }));
        break;
      }
      case 'hasAmountInAccount': {
        const hasAmountInAccount = question.label === 'Yes';
        if (this.state[stateName] !== hasAmountInAccount)
          this.setState(prevState => ({
            ...prevState,
            [stateName]: hasAmountInAccount,
            belowReqAmount: undefined,
            dateBelowReqAmount: { DD: null, MM: null, YYYY: null },
            dateBelowReqAmountError: ''
          }));
        break;
      }
      case 'belowReqAmount': {
        const belowReqAmount = question.label === 'Yes';
        if (this.state[stateName] !== belowReqAmount)
          this.setState(prevState => ({
            ...prevState,
            [stateName]: belowReqAmount,
            dateBelowReqAmount: { DD: null, MM: null, YYYY: null },
            dateBelowReqAmountError: ''
          }));
        break;
      }
      case 'hasClosingBalanceDate': {
        const hasClosingBalanceDate = question.label === 'Yes';
        if (this.state[stateName] !== hasClosingBalanceDate)
          this.setState(prevState => ({
            ...prevState,
            [stateName]: hasClosingBalanceDate,
            dateOfClosingBalance: { DD: null, MM: null, YYYY: null },
            dateOfClosingBalanceError: ''
          }));
        break;
      }
      case 'infoOnlyRequirementsOption':
        this.setState({
          infoOnlyRequirementsOption: question.label === 'Read a list of requirements' ? 'list' : 'checklist'
        });
        this.toggleAcknowledge('', false);
        break;
      default: {
        this.setState(prevState => ({
          ...prevState,
          [stateName]: question.label === 'Yes'
        }));
      }
    }
  };

  onHowToFixActionClicked = (question: IQuestion) => {
    const docName = bankStatementName(this.props.sponsorType);
    let key = question.label;
    if (question.label === 'I will fix the issues with my statement') {
      key = 'fixMyself';
      // the user can click on "edit" to fix the issues
      this.props.onAddRequiredDocument(key);
    } else if (question.label === 'I will ask my bank for a letter (recommended)') {
      key = 'askBankForLetter';
      this.props.onAddRequiredDocument(key);
    } else if (question.label === `I will have my ${docName.toLowerCase()} translated`) {
      key = 'getATranslation';
      this.props.onAddRequiredDocument(key);
    } else if (
      question.label.startsWith(`I will fix the issues with my statement AND I will have my`)
    ) {
      key = 'fixMyselfgetATranslation';
      this.props.onAddRequiredDocument(key);
    }
  };

  onCurrencyChange = (evt: any) => {
    const value = evt.currentTarget.value || '';
    const suggestions = currencies.filter((currency: ICurrency, index: number) =>
      currency.name_plural.toLowerCase().includes(value.toLowerCase())
    );

    this.setState({
      currencySuggestions: suggestions.map(suggestion => `${suggestion.name_plural} (${suggestion.code})`),
      currency: value
    });
  };

  onBlurCurrency = (event: React.FormEvent<HTMLInputElement>) => {
    const value = (event && event.currentTarget && event.currentTarget.value.trim()) || '';
    const hasFoundCurrency =
      currencies.filter((currency: ICurrency, index: number) =>
        `${currency.name_plural} (${currency.code})`.toLowerCase().includes(value.toLowerCase())
      ).length > 0;
    const currencyError = hasFoundCurrency ? '' : 'Please enter a currency';
    this.setState({
      currency: hasFoundCurrency ? value : '',
      currencyError
    });
  };

  onFocusCurrency = (event: React.FormEvent<HTMLInputElement>) => {
    this.setState({
      currencyError: ''
    });
  };

  onBlurDateBelowReqAmount = (event: React.FormEvent<HTMLInputElement>) => {
    const { DD, MM, YYYY } = this.state.dateBelowReqAmount;

    if (DD && MM && YYYY) {
      this.setState(state => {
        if (
          state.dateBelowReqAmount &&
          (state.dateBelowReqAmount.DD === null ||
            state.dateBelowReqAmount.MM === null ||
            state.dateBelowReqAmount.YYYY === null)
        ) {
          return {
            dateBelowReqAmountError: 'Please ensure the date is correct'
          };
        } else if (!isPastDate(state.dateBelowReqAmount)) {
          return {
            dateBelowReqAmountError: 'Date must be in the past'
          };
        } else {
          return null;
        }
      });
    }
  };

  onFocusDateBelowReqAmount = (event: React.FormEvent<HTMLInputElement>) => {
    this.setState({
      dateBelowReqAmountError: ''
    });
  };

  onBlurDateOfClosingBalance = (event: React.FormEvent<HTMLInputElement>) => {
    const { DD, MM, YYYY } = this.state.dateOfClosingBalance;

    if (DD && MM && YYYY) {
      this.setState(state => {
        if (
          state.dateOfClosingBalance &&
          (state.dateOfClosingBalance.DD === null ||
            state.dateOfClosingBalance.MM === null ||
            state.dateOfClosingBalance.YYYY === null)
        ) {
          return {
            dateOfClosingBalanceError: 'Please ensure the date is correct'
          };
        }
        const today = moment().startOf('day');
        const closingBalDate = moment(
          `${state.dateOfClosingBalance.YYYY}-${state.dateOfClosingBalance.MM}-${state.dateOfClosingBalance.DD}`,
          'YYYY-MM-DD'
        );
        // -1 allows for student to be in another timezone ahead of here by a day
        if (today.diff(closingBalDate, 'days') < -1) {
          return {
            dateOfClosingBalanceError: `Date should be in the past (or today's date)`
          };
        } else {
          return null;
        }
      });
    }
  };

  onFocusDateOfClosingBalance = (event: React.FormEvent<HTMLInputElement>) => {
    this.setState({
      dateOfClosingBalanceError: ''
    });
  };

  isFormValid = () => {
    const {
      isPaperBankStatement,
      isLogoShown,
      isDateShown,
      isNameAccShown,
      isInEnglish,
      hasProvidedTranslation,
      currency,
      hasAmountInAccount,
      belowReqAmount,
      hasClosingBalanceDate,
      hasRunningTransactions,
      dateBelowReqAmount,
      dateOfClosingBalance
    } = this.state;

    const isPaperBankStatementError = isPaperBankStatement !== undefined ? '' : 'Please select a value';
    const isLogoShownError = isLogoShown !== undefined ? '' : 'Please select a value';
    const isDateShownError = isDateShown !== undefined ? '' : 'Please select a value';
    const isNameAccShownError = isNameAccShown !== undefined ? '' : 'Please select a value';
    const isInEnglishError = isInEnglish !== undefined ? '' : 'Please select a value';
    const hasProvidedTranslationError =
      isInEnglish === false && hasProvidedTranslation === undefined ? 'Please select a value' : '';
    const currencyError = currency ? '' : 'Please fill in a currency';
    const hasAmountInAccountError = hasAmountInAccount !== undefined ? '' : 'Please select a value';
    const belowReqAmountError =
      hasAmountInAccount === true && belowReqAmount === undefined ? 'Please select a value' : '';
    const dateBelowReqAmountError =
      hasAmountInAccount === true &&
      belowReqAmount === true &&
      dateBelowReqAmount &&
      (dateBelowReqAmount.DD === null || dateBelowReqAmount.MM === null || dateBelowReqAmount.YYYY === null)
        ? 'Please fill in the date'
        : '';
    const hasRunningTransactionsError = hasRunningTransactions !== undefined ? '' : 'Please select a value';
    const hasClosingBalanceDateError = hasClosingBalanceDate !== undefined ? '' : 'Please select a value';
    const dateOfClosingBalanceError =
      hasClosingBalanceDate === true &&
      dateOfClosingBalance &&
      (dateOfClosingBalance.DD === null || dateOfClosingBalance.MM === null || dateOfClosingBalance.YYYY === null)
        ? 'Please fill in the date'
        : '';

    if (
      isPaperBankStatementError ||
      isLogoShownError ||
      isDateShownError ||
      isNameAccShownError ||
      isInEnglishError ||
      hasProvidedTranslationError ||
      currencyError ||
      hasAmountInAccountError ||
      belowReqAmountError ||
      dateBelowReqAmountError ||
      hasRunningTransactionsError ||
      hasClosingBalanceDateError ||
      dateOfClosingBalanceError
    ) {
      this.setState({
        isPaperBankStatementError,
        isLogoShownError,
        isDateShownError,
        isNameAccShownError,
        isInEnglishError,
        hasProvidedTranslationError,
        currencyError,
        hasAmountInAccountError,
        belowReqAmountError,
        dateBelowReqAmountError,
        hasRunningTransactionsError,
        hasClosingBalanceDateError,
        dateOfClosingBalanceError
      });
      return false;
    }
    return true;
  };

  onContinueClick = () => {
    const isFormValid = this.isFormValid();
    const highlightedErrorSections = getHighlightedErrorSections({
      ...this.state,
      dateBelowReqAmount: getStringFromDate(this.state.dateBelowReqAmount),
      dateOfClosingBalance: getStringFromDate(this.state.dateOfClosingBalance)
    });

    // get the correct options message
    const fixPossibleCount = highlightedErrorSections.includes('1.4') ? 1 : 0;
    const fixHardCount =
      (highlightedErrorSections.includes('1.1') ? 1 : 0) +
      (highlightedErrorSections.includes('1.2') ? 1 : 0) +
      (highlightedErrorSections.includes('2.1') ? 1 : 0) +
      (highlightedErrorSections.includes('4.1') ? 1 : 0) +
      (highlightedErrorSections.includes('4.2') ? 1 : 0);
    const translationRequiredCount = ['3.1', '3.2', '3.3', '3.4'].filter(val => highlightedErrorSections.includes(val))
      .length
      ? 1
      : 0;

    const maturityBelowReqAmountError =
      ['5.1', '5.2', '5.3'].filter(val => highlightedErrorSections.includes(val)).length > 0;
    const maturityClosingBalanceError = maturityBelowReqAmountError || highlightedErrorSections.includes('4.2');

    // Allow user to upload document, but show warnings once completed
    this.setState(
      {
        maturityBelowReqAmountError,
        maturityClosingBalanceError,
        fixPossibleCount,
        fixHardCount,
        translationRequiredCount,
        highlightedErrorSections,
        hasErrors: !isFormValid,
        questionsComplete: isFormValid
      },
      () => {
        if (isFormValid && !this.props.infoOnly) {
          this.onUpload();
        }
        if (this.props.infoOnly) {
          setTimeout(() => window.scrollTo(0, 0), 150); // timeout added for Chrome issue
          this.props.onAcknowledgeToggle('NOT_ACKNOWLEDGED', this.state.infoOnlyRequirementsOption, this.getMetaData());
        }
      }
    );
  };

  getWhatAreMyOptionsText = () => {
    const { fixPossibleCount, fixHardCount, translationRequiredCount } = this.state;

    const onlyFixPossible = fixPossibleCount > 0 && fixHardCount === 0 && translationRequiredCount === 0;
    const fixHardOnly = fixPossibleCount === 0 && fixHardCount > 0 && translationRequiredCount === 0;
    const translationRequiredOnly = fixPossibleCount === 0 && fixHardCount === 0 && translationRequiredCount > 0;

    const docName = bankStatementName(this.props.sponsorType);

    if (
      onlyFixPossible ||
      fixHardOnly ||
      (fixPossibleCount > 0 && fixHardCount > 0 && translationRequiredCount === 0)
    ) {
      return (
        <Text>
          You can choose to fix the above issues <b>OR</b> provide a letter from {this.getOwner()} bank confirming the
          funds have been in the account for at least 28 days.
        </Text>
      );
    } else if (translationRequiredOnly) {
      return (
        <Text>
          You can choose to provide a translation of {this.getOwner()} {docName.toLowerCase()} <b>OR</b> provide a
          letter from {this.getOwner()} bank confirming the funds have been in the account for at least 28 days.
        </Text>
      );
    } else if (
      (fixPossibleCount === 0 && fixHardCount > 0 && translationRequiredCount > 0) ||
      (fixPossibleCount > 0 && fixHardCount === 0 && translationRequiredCount > 0) ||
      (fixPossibleCount > 0 && fixHardCount > 0 && translationRequiredCount > 0)
    ) {
      return (
        <Text>
          You can choose to fix the above issues <b>AND</b> provide a translation <b>OR</b> provide a letter from{' '}
          {this.getOwner()} bank confirming the funds have been in the account for at least 28 days.
        </Text>
      );
    }
    return null;
  };

  getHowToFixOptions = (): IQuestion[] => {
    const { fixPossibleCount, fixHardCount, translationRequiredCount } = this.state;

    const onlyFixPossible = fixPossibleCount > 0 && fixHardCount === 0 && translationRequiredCount === 0;
    const fixHardOnly = fixPossibleCount === 0 && fixHardCount > 0 && translationRequiredCount === 0;
    const translationRequiredOnly = fixPossibleCount === 0 && fixHardCount === 0 && translationRequiredCount > 0;

    const docName = bankStatementName(this.props.sponsorType);

    if (
      onlyFixPossible ||
      fixHardOnly ||
      (fixPossibleCount > 0 && fixHardCount > 0 && translationRequiredCount === 0)
    ) {
      return [
        {
          char: '1',
          label: 'I will ask my bank for a letter (recommended)',
          selected: this.state.howToFixAction === 'askBankForLetter'
        },
        {
          char: '2',
          label: 'I will fix the issues with my statement',
          selected: this.state.howToFixAction === 'fixMyself'
        }
      ];
    } else if (translationRequiredOnly) {
      return [
        {
          char: '1',
          label: 'I will ask my bank for a letter (recommended)',
          selected: this.state.howToFixAction === 'askBankForLetter'
        },
        {
          char: '2',
          label: `I will have my ${docName.toLowerCase()} translated`,
          selected: this.state.howToFixAction === 'getATranslation'
        }
      ];
    } else if (
      (fixPossibleCount === 0 && fixHardCount > 0 && translationRequiredCount > 0) ||
      (fixPossibleCount > 0 && fixHardCount === 0 && translationRequiredCount > 0) ||
      (fixPossibleCount > 0 && fixHardCount > 0 && translationRequiredCount > 0)
    ) {
      return [
        {
          char: '1',
          label: 'I will ask my bank for a letter (recommended)',
          selected: this.state.howToFixAction === 'askBankForLetter'
        },
        {
          char: '2',
          label: `I will fix the issues with my statement AND I will have my ${docName.toLowerCase()} translated`,
          selected: this.state.howToFixAction === 'fixMyselfgetATranslation'
        }
      ];
    }
    return [];
  };

  getWhatDoIHaveToDoText = () => {
    const { howToFixAction } = this.state;

    // edge case when (howToFixAction === 'fixMyselfgetATranslation')
    // edge case when (howToFixAction includes 'fixMyself') AND Maturity date issue
    const actions = [];
    const statementName = bankStatementName(this.props.sponsorType);
    const transName = bankStatementTransName(this.props.sponsorType);

    if (howToFixAction && howToFixAction.includes('askBankForLetter')) {
      const { accountOwner } = this.props;
      const guardianMessage =
        accountOwner === 'Mother' || accountOwner === 'Father' || accountOwner === 'Guardian'
          ? 'Parents bank'
          : accountOwner === 'Spouse'
          ? 'Partners bank'
          : 'Bank';
      actions.push(
        <React.Fragment key="ask-for-bank-letter">
          <UploadIntro>{guardianMessage} letter added to finance document upload section.</UploadIntro>

          <Text>
            Once you upload this letter from the bank, you will not have to do anything further with {this.getOwner()}{' '}
            {statementName.toLowerCase()}.
          </Text>
        </React.Fragment>
      );
    } else if (howToFixAction && howToFixAction.includes('getATranslation')) {
      actions.push(
        <React.Fragment key="get-translation">
          <UploadIntro>
            A new document has been added to {this.getOwner()} finance documents section: {transName}.
          </UploadIntro>

          <Text>
            Please review the requirements
            {!this.props.infoOnly && ` and upload once you have the required documents`}.
          </Text>
        </React.Fragment>
      );
    } else if (howToFixAction && howToFixAction.includes('fixMyself') && !this.props.infoOnly) {
      actions.push(
        <React.Fragment key="fix-myself">
          <Text>
            You will need to <b>upload a new {statementName.toLowerCase()}</b> after you have fixed the issues with the
            current statement.
          </Text>
        </React.Fragment>
      );
    }
    // if maturity issue then show further info
    if (
      !howToFixAction?.includes('askBankForLetter') &&
      (this.state.hasAmountInAccount === false ||
        this.state.maturityBelowReqAmountError ||
        this.state.maturityClosingBalanceError)
    ) {
      actions.push(
        <React.Fragment key="fix-maturity">
          {this.state.maturityBelowReqAmountError || this.state.maturityClosingBalanceError ? (
            <Text>
              Request a new statement from the bank{' '}
              <b>
                {this.props.document.hasAmountInAccount === false || this.state.hasAmountInAccount === false
                  ? '28 days after the full amount is visible in the account'
                  : this.state.maturityBelowReqAmountError && this.props.document.belowReqAmount === true
                  ? `after ${
                      getStringFromDate(this.state.dateBelowReqAmount) !== ''
                        ? getMaturityDate(getStringFromDate(this.state.dateBelowReqAmount)).format('DD MMM YYYY')
                        : getMaturityDate(this.props.document.dateBelowReqAmount).format('DD MMM YYYY')
                    }`
                  : this.state.maturityClosingBalanceError &&
                    this.props.document.belowReqAmount === false &&
                    this.state.hasClosingBalanceDate === true
                  ? `that is up to date`
                  : this.state.maturityClosingBalanceError && this.state.hasClosingBalanceDate === true
                  ? `after ${getMaturityDate().format('DD MMM YYYY')}`
                  : `with a recent date on it (within 31 days)`}
              </b>
              , this should mark 28 days that the required amount has been in the account
            </Text>
          ) : (
            <Text>
              Request a new statement where the full required amount has been in the account for a full 28 days.
            </Text>
          )}

          <Text>Ensure that the balance does not fall below the required amount or the 28 day period will reset.</Text>
        </React.Fragment>
      );
    }
    return actions;
  };

  getHowToFixText = () => {
    const { howToFixAction } = this.state;

    if (howToFixAction === 'askBankForLetter') {
      return <>Next steps</>;
    } else {
      return <>What you need to do</>;
    }
  };

  onFixUpload = () => {
    this.setState(
      {
        highlightedErrorSections: [],
        hasErrors: false
      },
      () => this.onUpload(true)
    );
  };

  onUploadAfterApproved = () => {
    const metaData = {
      hasBankStatement: true,
      isPaperBankStatement: true,
      isLogoShown: true,
      isDateShown: true,
      isNameAccShown: true,
      isInEnglish: true,
      hasProvidedTranslation: undefined,
      currency: undefined,
      hasAmountInAccount: true,
      belowReqAmount: false,
      dateBelowReqAmount: undefined,
      hasRunningTransactions: true,
      hasClosingBalanceDate: true,
      dateOfClosingBalance: undefined
    };
    this.props.onUpload(metaData);
  };

  getMetaData = (isFix?: boolean) => {
    const {
      hasBankStatement,
      isPaperBankStatement,
      isLogoShown,
      isDateShown,
      isNameAccShown,
      isInEnglish,
      hasProvidedTranslation,
      currency,
      hasAmountInAccount,
      belowReqAmount,
      dateBelowReqAmount,
      hasRunningTransactions,
      hasClosingBalanceDate,
      dateOfClosingBalance,
      highlightedErrorSections
    } = this.state;

    return {
      hasBankStatement,
      isPaperBankStatement,
      isLogoShown,
      isDateShown,
      isNameAccShown,
      isInEnglish,
      hasProvidedTranslation: isInEnglish === false ? hasProvidedTranslation === true : undefined,
      currency,
      hasAmountInAccount,
      belowReqAmount,
      dateBelowReqAmount: isFix ? undefined : getStringFromDate(dateBelowReqAmount),
      hasRunningTransactions,
      hasClosingBalanceDate,
      dateOfClosingBalance: getStringFromDate(dateOfClosingBalance),
      shouldShowRequirements: highlightedErrorSections.length > 0,
      isDocumentFixed: !!isFix
    };
  };

  onUpload = (isFix?: boolean) => {
    this.props.onUpload(this.getMetaData(isFix));
  };

  getOwner = (capitalize?: boolean) =>
    this.props.accountOwner === 'Mother'
      ? capitalize
        ? `Your Mother's`
        : `your Mother's`
      : this.props.accountOwner === 'Father'
      ? capitalize
        ? `Your Father's`
        : `your Father's`
      : this.props.accountOwner === 'Guardian'
      ? capitalize
        ? `Your Guardian's`
        : `your Guardian's`
      : this.props.accountOwner === 'Spouse'
      ? capitalize
        ? `Your Partner's`
        : `your Partner's`
      : capitalize
      ? 'Your'
      : 'your';

  handleOnClose = () => {
    if (this.props.docStatus === 'INFO_ONLY') {
      this.props.onAcknowledgeToggle('NOT_ACKNOWLEDGED', this.state.infoOnlyRequirementsOption);
    }
    this.props.onClose();
  };

  toggleAcknowledge = (key: string, value: boolean) => {
    // prevent call to API if previous call not yet complete
    if (
      (value === true && this.props.docStatus !== 'ACKNOWLEDGED') ||
      (value === false && this.props.docStatus === 'ACKNOWLEDGED')
    ) {
      this.props.onAcknowledgeToggle(
        value === true ? 'ACKNOWLEDGED' : 'NOT_ACKNOWLEDGED',
        this.state.infoOnlyRequirementsOption
      );
    }
  };

  renderQuestions = () => {
    const {
      isPaperBankStatement,
      isPaperBankStatementError,
      isLogoShown,
      isLogoShownError,
      isDateShown,
      isDateShownError,
      isNameAccShown,
      isNameAccShownError,
      isInEnglish,
      isInEnglishError,
      hasProvidedTranslation,
      hasProvidedTranslationError,
      currency,
      currencyError,
      currencySuggestions,
      hasAmountInAccount,
      hasAmountInAccountError,
      belowReqAmount,
      belowReqAmountError,
      dateBelowReqAmount,
      dateBelowReqAmountError,
      hasRunningTransactions,
      hasRunningTransactionsError,
      hasClosingBalanceDate,
      hasClosingBalanceDateError,
      dateOfClosingBalance,
      dateOfClosingBalanceError
    } = this.state;

    const { sponsorAmount, sponsorType } = this.props;
    const docName = bankStatementName(sponsorType);

    return (
      <>
        <RadioOptionsWithConfirm
          id="isPaperBankStatement"
          label={`Is the ${docName.toLowerCase()} a paper statement from the bank or did you get it from the internet?`}
          errorText={isPaperBankStatementError}
          questions={[
            {
              char: '1',
              label: 'Paper',
              selected: isPaperBankStatement === true
            },
            {
              char: '2',
              label: 'Internet',
              selected: isPaperBankStatement === false
            }
          ]}
          isInline={true}
          onClick={(question: IQuestion) => this.onQuestionButtonClick(question, 'isPaperBankStatement')}
        />

        <RadioOptionsWithConfirm
          id="isLogoShown"
          label="Does the statement show the logo of the bank?"
          errorText={isLogoShownError}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: isLogoShown === true
            },
            {
              char: 'N',
              label: 'No',
              selected: isLogoShown === false
            }
          ]}
          isInline={true}
          onClick={(question: IQuestion) => this.onQuestionButtonClick(question, 'isLogoShown')}
        />

        <RadioOptionsWithConfirm
          id="isDateShown"
          label="Does the statement show a date at the top of the page?"
          errorText={isDateShownError}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: isDateShown === true
            },
            {
              char: 'N',
              label: 'No',
              selected: isDateShown === false
            }
          ]}
          isInline={true}
          onClick={(question: IQuestion) => this.onQuestionButtonClick(question, 'isDateShown')}
        />

        <RadioOptionsWithConfirm
          id="isNameAccShown"
          label={`Does the statement show ${this.getOwner()} name and the account number?`}
          errorText={isNameAccShownError}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: isNameAccShown === true
            },
            {
              char: 'N',
              label: 'No',
              selected: isNameAccShown === false
            }
          ]}
          isInline={true}
          onClick={(question: IQuestion) => this.onQuestionButtonClick(question, 'isNameAccShown')}
        />

        <RadioOptionsWithConfirm
          id="isInEnglish"
          label="Is the statement in English?"
          errorText={isInEnglishError}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: isInEnglish === true
            },
            {
              char: 'N',
              label: 'No',
              selected: isInEnglish === false
            }
          ]}
          isInline={true}
          onClick={(question: IQuestion) => this.onQuestionButtonClick(question, 'isInEnglish')}
        />

        {isInEnglish === false && (
          <RadioOptionsWithConfirm
            label={`Have you provided an English translation of the ${docName.toLowerCase()}?`}
            errorText={hasProvidedTranslationError}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: hasProvidedTranslation === true
              },
              {
                char: 'N',
                label: 'No',
                selected: hasProvidedTranslation === false
              }
            ]}
            isInline={true}
            onClick={(question: IQuestion) => this.onQuestionButtonClick(question, 'hasProvidedTranslation')}
          />
        )}

        <AutocompleteInputWithConfirm
          id="currency"
          label="What is the currency?"
          onChange={(evt: any) => this.onCurrencyChange(evt)}
          value={currency}
          showErrorText={!!currencyError}
          errorText={currencyError}
          suggestions={currencySuggestions}
          onBlur={(evt: any) => this.onBlurCurrency(evt)}
          onFocus={(evt: any) => this.onFocusCurrency(evt)}
        />

        <RadioOptionsWithConfirm
          id="bankStatementModal-amountInAccount"
          label={`Is there £${formatCurrency(sponsorAmount!)} in the account now?`}
          errorText={hasAmountInAccountError}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: hasAmountInAccount === true
            },
            {
              char: 'N',
              label: 'No',
              selected: hasAmountInAccount === false
            }
          ]}
          isInline={true}
          onClick={(question: IQuestion) => this.onQuestionButtonClick(question, 'hasAmountInAccount')}
        />

        {hasAmountInAccount === true && (
          <RadioOptionsWithConfirm
            id="bankStatementModal-balanceBelowAmount"
            label={`Was the account balance below £${formatCurrency(sponsorAmount!)} at any time in the past 60 days?`}
            errorText={belowReqAmountError}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: belowReqAmount === true
              },
              {
                char: 'N',
                label: 'No',
                selected: belowReqAmount === false
              }
            ]}
            isInline={true}
            onClick={(question: IQuestion) => this.onQuestionButtonClick(question, 'belowReqAmount')}
          />
        )}

        {belowReqAmount === true && (
          <DateInputWithConfirm
            id="dateBelowReqAmount"
            value={dateBelowReqAmount}
            label={`When was the last time the balance was below £${formatCurrency(sponsorAmount!)}?`}
            errorText={dateBelowReqAmountError}
            onChangeDD={(evt: any) =>
              this.setState({
                dateBelowReqAmount: {
                  ...dateBelowReqAmount,
                  DD: evt.target.value
                }
              })
            }
            onChangeMM={(evt: any) =>
              this.setState({
                dateBelowReqAmount: {
                  ...dateBelowReqAmount,
                  MM: evt.target.value
                }
              })
            }
            onChangeYY={(evt: any) =>
              this.setState({
                dateBelowReqAmount: {
                  ...dateBelowReqAmount,
                  YYYY: evt.target.value
                }
              })
            }
            onBlur={this.onBlurDateBelowReqAmount}
            onFocus={this.onFocusDateBelowReqAmount}
          />
        )}

        <RadioOptionsWithConfirm
          id="hasRunningTransactions"
          label="Does the statement show a running total alongside all transactions?"
          errorText={hasRunningTransactionsError}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: hasRunningTransactions === true
            },
            {
              char: 'N',
              label: 'No',
              selected: hasRunningTransactions === false
            }
          ]}
          isInline={true}
          onClick={(question: IQuestion) => this.onQuestionButtonClick(question, 'hasRunningTransactions')}
        />

        <RadioOptionsWithConfirm
          id="hasClosingBalanceDate"
          label="Does the closing balance at the bottom of the statement have a date alongside it?"
          errorText={hasClosingBalanceDateError}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: hasClosingBalanceDate === true
            },
            {
              char: 'N',
              label: 'No',
              selected: hasClosingBalanceDate === false
            }
          ]}
          isInline={true}
          onClick={(question: IQuestion) => this.onQuestionButtonClick(question, 'hasClosingBalanceDate')}
        />

        {hasClosingBalanceDate === true && (
          <DateInputWithConfirm
            id="dateOfClosingBalance"
            value={dateOfClosingBalance}
            label="What is the date next to the closing balance?"
            errorText={dateOfClosingBalanceError}
            onChangeDD={(evt: any) =>
              this.setState({
                dateOfClosingBalance: {
                  ...dateOfClosingBalance,
                  DD: evt.target.value
                }
              })
            }
            onChangeMM={(evt: any) =>
              this.setState({
                dateOfClosingBalance: {
                  ...dateOfClosingBalance,
                  MM: evt.target.value
                }
              })
            }
            onChangeYY={(evt: any) => {
              this.setState({
                dateOfClosingBalance: {
                  ...dateOfClosingBalance,
                  YYYY: evt.target.value
                }
              });
            }}
            onBlur={this.onBlurDateOfClosingBalance}
            onFocus={this.onFocusDateOfClosingBalance}
          />
        )}

        <Flex justifyContent="end">
          <Button
            id="bank-statement-continue-button"
            isDisabled={dateOfClosingBalanceError === '' && dateBelowReqAmountError === '' ? false : true}
            onClick={this.onContinueClick}
            variant="solid"
            size="lg"
          >
            Continue
          </Button>
        </Flex>
      </>
    );
  };

  renderFixOptions = () => {
    const { howToFixAction, howToFixActionError } = this.state;
    const docName = bankStatementName(this.props.sponsorType);

    return (
      <>
        {this.getWhatAreMyOptionsText() !== null && (
          <RequirementsRemedy title="What are my options?">{this.getWhatAreMyOptionsText()}</RequirementsRemedy>
        )}

        {this.getHowToFixOptions().length > 0 && (
          <RadioOptionsWithConfirm
            label="What would you like to do?"
            errorText={howToFixActionError}
            questions={this.getHowToFixOptions()}
            onClick={(question: IQuestion) => this.onHowToFixActionClicked(question)}
          />
        )}

        {((howToFixAction !== '' && howToFixAction !== undefined) || this.getWhatDoIHaveToDoText().length > 0) && (
          <>
            <RequirementsRemedy title={this.getHowToFixText()}>{this.getWhatDoIHaveToDoText()}</RequirementsRemedy>

            {((!howToFixAction?.includes('askBankForLetter') &&
              (this.state.hasAmountInAccount === false ||
                this.state.maturityBelowReqAmountError ||
                this.state.maturityClosingBalanceError)) ||
              howToFixAction?.includes('fixMyself')) &&
              !this.props.infoOnly && (
                <UploadButtonSection onUpload={this.onFixUpload}>
                  {this.state.hasAmountInAccount === false ? (
                    <>
                      Upload new <b>{docName.toLowerCase()}</b> 28 days after full amount is deposited
                    </>
                  ) : this.state.maturityBelowReqAmountError && this.props.document.belowReqAmount === true ? (
                    <>
                      Upload new <b>{docName.toLowerCase()}</b> after{' '}
                      {getStringFromDate(this.state.dateBelowReqAmount) !== ''
                        ? getMaturityDate(getStringFromDate(this.state.dateBelowReqAmount)).format('DD MMM YYYY')
                        : getMaturityDate(this.props.document.dateBelowReqAmount).format('DD MMM YYYY')}
                    </>
                  ) : (
                    <>
                      Upload new <b>{docName}</b>
                    </>
                  )}
                </UploadButtonSection>
              )}
          </>
        )}
      </>
    );
  };

  renderContent = () => {
    const {
      questionsComplete,
      hasErrors,
      highlightedErrorSections,
      hasBankStatement,
      hasBankStatementError,
      howToFixAction,
      infoOnlyRequirementsOption
    } = this.state;

    const { docStatus, infoOnly, docKey, onClose, onContinue, fileNames, sponsorType } = this.props;

    const isSecondUpload = docStatus !== 'AWAITING' && hasErrors === false && questionsComplete;
    const docName = bankStatementName(sponsorType);

    return (
      <>
        {infoOnly ? (
          <>
            <Text>
              A <b>{docName.toLowerCase()}</b> may be required during your visa application to prove you have access to
              enough funds to cover your course fees and living expenses.
            </Text>

            <RadioOptionsWithConfirm
              id="infoOnlyRequiremnentsOption"
              label="There are two ways you can check the requirements of this document"
              questions={[
                {
                  char: '1',
                  label: 'Read a list of requirements',
                  selected: infoOnlyRequirementsOption === 'list'
                },
                {
                  char: '2',
                  label: 'Use our online document checklist',
                  selected: infoOnlyRequirementsOption === 'checklist'
                }
              ]}
              onClick={(question: IQuestion) => this.onQuestionButtonClick(question, 'infoOnlyRequirementsOption')}
            />

            {infoOnlyRequirementsOption === 'list' ? (
              <>
                <Requirements uploadComplete={false} highlightedSections={[]} />

                <AcknowledgeBox
                  id={docKey}
                  blurb="Please confirm you have read and understand the above."
                  isTicked={docStatus === 'ACKNOWLEDGED'}
                  onChange={this.toggleAcknowledge}
                />
              </>
            ) : null}

            {infoOnlyRequirementsOption === 'checklist' ? (
              <>
                {!questionsComplete ? (
                  <>{this.renderQuestions()}</>
                ) : (
                  <>
                    {this.state.highlightedErrorSections.length ? (
                      <>
                        <Requirements
                          uploadComplete={questionsComplete}
                          highlightedSections={this.state.highlightedErrorSections}
                        />

                        {this.renderFixOptions()}
                      </>
                    ) : (
                      <RequirementsRemedy isSatisfied>
                        <Text>
                          Based on the responses provided by you, your document appears to meet requirements at this
                          point in time.
                        </Text>

                        <Text>
                          If you would like to check the requirements of this document manually, you can select "read a
                          list of requirements" in the above options.
                        </Text>
                      </RequirementsRemedy>
                    )}

                    <AcknowledgeBox
                      id={docKey}
                      blurb="Please confirm you have read and understand the above."
                      isTicked={docStatus === 'ACKNOWLEDGED'}
                      onChange={this.toggleAcknowledge}
                    />
                  </>
                )}
              </>
            ) : null}

            {infoOnlyRequirementsOption === 'list' ||
            (infoOnlyRequirementsOption === 'checklist' && questionsComplete) ? (
              <UploadActions
                complete={docStatus === 'ACKNOWLEDGED'}
                onContinue={this.handleOnClose}
                label="Return to document upload"
              />
            ) : null}
          </>
        ) : (
          <>
            {!questionsComplete && docStatus === 'AWAITING' ? (
              <>
                <RadioOptionsWithConfirm
                  id="bankStatementModal"
                  label={`Have you got a ${docName.toLowerCase()} yet?`}
                  errorText={hasBankStatementError}
                  questions={[
                    {
                      char: 'Y',
                      label: 'Yes',
                      selected: hasBankStatement === true
                    },
                    {
                      char: 'N',
                      label: 'No',
                      selected: hasBankStatement === false
                    }
                  ]}
                  isInline={true}
                  onClick={(question: IQuestion) => this.onQuestionButtonClick(question, 'hasBankStatement')}
                />
              </>
            ) : (
              docStatus === 'AWAITING' && (
                <>
                  <UploadButtonSection onUpload={this.onUpload}>
                    Awaiting upload of <b>Personal {docName}</b>
                  </UploadButtonSection>

                  <UploadActions complete={true} onContinue={onContinue} />
                </>
              )
            )}

            {hasBankStatement === true && !questionsComplete && docStatus === 'AWAITING' ? (
              <>{this.renderQuestions()}</>
            ) : null}

            {(hasBankStatement === false && !questionsComplete) || (docStatus !== 'AWAITING' && hasErrors) ? (
              <>
                {hasErrors ? (
                  <UploadIntro uploadWarning={true}>
                    {this.getOwner(true)} statement <b>does not meet all requirements</b> therefore further action is
                    required.
                  </UploadIntro>
                ) : (
                  <Text>
                    It is critical that {this.getOwner()} {docName.toLowerCase()} meets the following requirements:
                  </Text>
                )}

                <Requirements
                  uploadComplete={docStatus !== 'AWAITING'}
                  highlightedSections={highlightedErrorSections}
                />

                {hasErrors && <>{this.renderFixOptions()}</>}

                <UploadActions
                  complete={howToFixAction !== 'fixMyself'}
                  onContinue={onClose}
                  label="Return to document upload"
                />
              </>
            ) : isSecondUpload ? (
              <UploadCompleteSection fileNames={fileNames} onUpload={this.onFixUpload} onContinue={onClose} />
            ) : docStatus === 'APPROVED' ? (
              <UploadCompleteSection fileNames={fileNames} onUpload={this.onUploadAfterApproved} onContinue={onClose} />
            ) : docStatus === 'UPLOADED_OK' ? (
              <>
                <UploadButtonSection onUpload={this.onFixUpload} buttonLabelText={'Add additional files'}>
                  <b>Uploaded by University</b>
                </UploadButtonSection>

                <UploadActions complete={true} onContinue={onClose} label="Finish" />
              </>
            ) : null}
          </>
        )}
      </>
    );
  };

  render() {
    const { sectionTitle } = this.props;

    return (
      <ModalColumns
        id={'bankStatement'}
        title={sectionTitle}
        onClose={this.handleOnClose}
        supportingColumn={<UploadExample altText="Bank statement example" url={bankStatementExample} />}
      >
        {this.renderContent()}
      </ModalColumns>
    );
  }
}

export default BankStatementModal;
